import { t } from "i18next";
import { isUndefined } from "lodash";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { DateRange } from "react-day-picker";
import { useParams } from "react-router-dom";
import { useFetch } from "../../hooks/useFetch";
import { useAppStore } from "../../stores/RootStore";
import { Device } from "../../types/types";
import { Button } from "../../ui/Button";
import { DatePickerWithRange } from "../../ui/Datepicker/DateRangePicker";
import { ScrollArea } from "../../ui/ScrollArea/ScrollArea";
import Spinner from "../../ui/Spinner/Spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/Tabs/Tabs";
import DeviceConstantsTab from "./deviceItemTabs/deviceConstantsTab/DeviceConstantsTab";
import DeviceIntensivity from "./deviceItemTabs/deviceNetworkTab/DeviceIntensivity";
import DeviceSlot from "./deviceItemTabs/deviceNetworkTab/DeviceSlot";
import DeviceSlotDebug from "./deviceItemTabs/deviceNetworkTab/DeviceSlotDebug";
import ExportForm from "./ExportForm";
import { CSVLink } from "react-csv";

type Props = {};

const DeviceItem = (props: Props) => {
  const { id } = useParams();
  const devices = useAppStore().devices;

  const [currentItem, setCurrentItem] = useState<Device>(
    devices?.find((d) => d.id === id)!,
  );
  const [selectedDate, setSelectedDate] = useState<DateRange | any>({
    from: DateTime.local().startOf("day").toUTC().toJSDate(),
    to: DateTime.local().toUTC().toJSDate(),
  });

  const [loading, setLoading] = useState(true);
  const [csvData, setCsvData] = useState<string[][]>();

  const setDateWithChecking = (value: DateRange | any) => {
    if (isUndefined(value)) {
      setSelectedDate({
        from: DateTime.fromJSDate(selectedDate.from).startOf("day").toJSDate(),
        to: DateTime.fromJSDate(selectedDate.from).endOf("day").toJSDate(),
      });
      return;
    }
    if (isUndefined(value.from))
      setSelectedDate({
        from: DateTime.local().startOf("day").toJSDate(),
        to: DateTime.local().toJSDate(),
      });
    if (isUndefined(value.to))
      setSelectedDate({
        from: DateTime.fromJSDate(value.from).startOf("day").toJSDate(),
        to: DateTime.fromJSDate(value.from).endOf("day").toJSDate(),
      });
    else setSelectedDate(value);
  };

  const [{ response, isLoading, error }, doFetchItem] = useFetch(
    `/devices/devices/${id}`,
  );

  useEffect(() => {
    if (!id) return;
    doFetchItem();
    setLoading(true);
  }, [id]);

  useEffect(() => {
    if (!response) return;

    setCurrentItem(response.data as unknown as Device);
    setLoading(false);
  }, [response]);

  let [isExportOpen, setIsExportOpen] = useState(false);

  function openExportModal() {
    setIsExportOpen(true);
  }

  function closeExportModal() {
    setIsExportOpen(false);
  }

  return loading ? (
    <Spinner />
  ) : (
    <>
      <ScrollArea className="w-full">
        <div className="flex flex-col w-full h-full max-w-[1200px] mx-auto px-8">
          <div className="flex flex-none w-full h-24 text-base gap-2 items-center justify-between">
            <div className="flex flex-col space-y-1">
              <span className="text-sm">{t("device")}</span>
              <span className="text-xl font-semibold font-mono">
                {currentItem?.attributes.serial_number}
              </span>
            </div>
          </div>
          <Tabs defaultValue="main">
            <TabsList className="w-full flex-none h-10 justify-between px-2 rounded">
              <div className="flex">
                <TabsTrigger value="main">{t("data")}</TabsTrigger>
                <TabsTrigger value="constants">{t("constants")}</TabsTrigger>
                <TabsTrigger value="debug">{t("debug")}</TabsTrigger>
              </div>
              <div className="flex space-x-2">
                {csvData && (
                  <CSVLink
                    data={csvData}
                    filename={"export.csv"}
                    separator={";"}
                  >
                    <Button variant="outline" size="datepicker">
                      {t("save_to_csv")}
                    </Button>
                  </CSVLink>
                )}
                <Button
                  variant={"outline"}
                  size={"datepicker"}
                  onClick={() => openExportModal()}
                >
                  {t("export")}
                </Button>
                <DatePickerWithRange
                  mode="range"
                  selected={selectedDate}
                  disabled={{
                    after: DateTime.local().toJSDate(),
                  }}
                  onSelect={(value) => setDateWithChecking(value)}
                />
              </div>
            </TabsList>

            <TabsContent value="main">
              <div className="h-full max-w-[1280px] mx-auto">
                <DeviceIntensivity
                  currentDevice={currentItem}
                  selectedDate={selectedDate}
                  setSelectedDate={setDateWithChecking}
                />
                {currentItem?.attributes?.slots?.length! > 1 ? (
                  currentItem?.attributes?.slots?.map((slot) => (
                    <DeviceSlot slot={slot} selectedDate={selectedDate} />
                  ))
                ) : (
                  <DeviceSlot
                    slot={currentItem?.attributes?.slots![0]}
                    selectedDate={selectedDate}
                  />
                )}
              </div>
            </TabsContent>

            <TabsContent value="constants">
              <DeviceConstantsTab id={id} currentDevice={currentItem} />
            </TabsContent>
            <TabsContent value="debug">
              <div className="h-full max-w-[1280px] mx-auto">
                {currentItem?.attributes?.slots?.length! > 1 ? (
                  currentItem?.attributes?.slots?.map((slot) => (
                    <DeviceSlotDebug slot={slot} selectedDate={selectedDate} />
                  ))
                ) : (
                  <DeviceSlotDebug
                    slot={currentItem?.attributes?.slots![0]}
                    selectedDate={selectedDate}
                  />
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </ScrollArea>
      {isExportOpen && (
        <ExportForm
          isExportOpen={isExportOpen}
          closeModal={closeExportModal}
          currentItem={currentItem}
          setCsvData={setCsvData}
        />
      )}
    </>
  );
};

export default DeviceItem;
